import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Download, Search, UserX, FileText, AlertTriangle } from "lucide-react";

const ComplianceTools = () => {
  const [userEmail, setUserEmail] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleDataExport = async () => {
    if (!userEmail) {
      toast({
        title: "Email Required",
        description: "Please enter a user email address.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Get user data from profiles
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', userEmail)
        .single();

      if (profileError || !profile) {
        throw new Error('User not found');
      }

      // Get additional user data
      const userId = profile.user_id;
      
      // Fetch all user-related data
      const [
        { data: sessions },
        { data: credits },
        { data: reviews },
        { data: notifications },
        { data: tickets }
      ] = await Promise.all([
        supabase.from('sessions').select('*').eq('student_id', userId),
        supabase.from('student_credits').select('*').eq('user_id', userId),
        supabase.from('session_reviews').select('*').eq('student_id', userId),
        supabase.from('notifications').select('*').eq('user_id', userId),
        supabase.from('support_tickets').select('*').eq('user_id', userId)
      ]);

      // Compile data export
      const exportData = {
        profile,
        sessions: sessions || [],
        credits: credits || [],
        reviews: reviews || [],
        notifications: notifications || [],
        support_tickets: tickets || [],
        export_date: new Date().toISOString(),
        export_type: 'GDPR_DATA_REQUEST'
      };

      // Download as JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_data_export_${userEmail}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Data Export Successful",
        description: "User data has been exported and downloaded.",
      });

    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export user data. Please check the email address.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAccountDeletion = async () => {
    if (!userEmail || !deleteReason) {
      toast({
        title: "Missing Information",
        description: "Please enter both email and deletion reason.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // This would need to be implemented as an edge function for proper user deletion
      // For now, we'll just show a confirmation
      toast({
        title: "Deletion Request Logged",
        description: "Account deletion request has been logged. Manual processing required.",
        variant: "destructive",
      });

      // Log the deletion request (you could add a deletion_requests table)
      console.log('Account deletion requested:', {
        email: userEmail,
        reason: deleteReason,
        requested_at: new Date().toISOString()
      });

    } catch (error) {
      console.error('Deletion error:', error);
      toast({
        title: "Deletion Failed",
        description: "Failed to process deletion request.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <AlertTriangle className="w-5 h-5 text-primary" />
        <h2 className="text-2xl font-bold">GDPR Compliance Tools</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Data Export Tool */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Data Export (Article 20)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="export-email">User Email</Label>
              <Input
                id="export-email"
                type="email"
                placeholder="user@example.com"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
              />
            </div>
            <Button 
              onClick={handleDataExport}
              disabled={loading}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Export User Data
            </Button>
            <p className="text-xs text-muted-foreground">
              Exports all user data in JSON format for GDPR compliance.
            </p>
          </CardContent>
        </Card>

        {/* Account Deletion Tool */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="w-4 h-4" />
              Account Deletion (Article 17)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="delete-email">User Email</Label>
              <Input
                id="delete-email"
                type="email"
                placeholder="user@example.com"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="delete-reason">Deletion Reason</Label>
              <Textarea
                id="delete-reason"
                placeholder="User requested account deletion via GDPR Article 17..."
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
              />
            </div>
            <Button 
              onClick={handleAccountDeletion}
              disabled={loading}
              variant="destructive"
              className="w-full"
            >
              <UserX className="w-4 h-4 mr-2" />
              Log Deletion Request
            </Button>
            <p className="text-xs text-muted-foreground">
              Logs account deletion request for manual processing.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Compliance Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-green-500 mb-1">✓</div>
              <div className="text-sm font-medium">Privacy Policy</div>
              <div className="text-xs text-muted-foreground">GDPR Enhanced</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-green-500 mb-1">✓</div>
              <div className="text-sm font-medium">Cookie Policy</div>
              <div className="text-xs text-muted-foreground">Comprehensive</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-green-500 mb-1">✓</div>
              <div className="text-sm font-medium">Terms of Use</div>
              <div className="text-xs text-muted-foreground">Payment Disclosures</div>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-primary/10 rounded-lg">
            <h3 className="font-semibold mb-2">Compliance Checklist:</h3>
            <ul className="space-y-1 text-sm">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Legal basis for data processing documented
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Data retention policies defined
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                International transfer safeguards in place
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                User rights procedures established
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Data breach notification procedures ready
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Payment processing disclosures complete
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplianceTools;